/**
 * Infrastructure Layer - Medical Reservation API
 * 診療予約CSVデータ処理・登録APIクライアント
 */

import apiClient from '../../utils/apiClient';
import type { CsvProcessResponse } from '../../types/medicalReservationProcess';
import { CSV_TO_DISPLAY_MAPPING } from '../../constants/csvHeaders';

/**
 * API設定
 */
const API_ENDPOINTS = {
  PROCESS_CSV: '/api/medical-reservations/csv/process',
  REGISTER_CSV: '/api/medical-list/csv/register',
  CHECK_STATUS: (id: string) => `/api/medical-list/csv/register/${id}`,
} as const;

/**
 * API呼び出しオプション
 */
const API_OPTIONS = {
  /** タイムアウト（ミリ秒） */
  timeout: 120000,
  /** Cookie送信を無効化 */
  withCredentials: false,
} as const;

/**
 * CSVヘッダーを正規化する
 * バックエンドAPIが期待するヘッダー名に変換する
 * @param header 元のCSVヘッダー名
 * @returns 正規化されたヘッダー名
 */
const normalizeHeader = (header: string): string => {
  const trimmed = header.trim();
  return CSV_TO_DISPLAY_MAPPING[trimmed] || trimmed;
};

/**
 * 登録リクエスト型
 */
export interface RegisterCsvRequest {
  readonly csvData: {
    readonly headers: string[];
    readonly rows: string[][];
  };
  readonly metadata: {
    readonly fileName: string;
    readonly fileSize: number;
    readonly encoding: 'UTF-8' | 'Shift-JIS' | 'auto';
    readonly uploadedAt: string;
  };
  readonly user_id?: number;
}

/**
 * 登録レスポンス型
 */
export interface RegisterCsvResponse {
  readonly success: boolean;
  readonly data?: {
    readonly registrationId: string;
    readonly status: string;
    readonly results?: {
      readonly successCount: number;
      readonly failureCount: number;
      readonly errors: ReadonlyArray<{
        readonly row: number;
        readonly error: string;
        readonly data: string[];
      }>;
    };
    readonly processingTime?: number;
  };
  readonly error?: {
    readonly code: string;
    readonly message: string;
    readonly details?: unknown;
  };
}

/**
 * ステータスレスポンス型
 */
export interface StatusCheckResponse {
  readonly success: boolean;
  readonly data?: {
    readonly registrationId: string;
    readonly status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    readonly progress?: {
      readonly currentRow: number;
      readonly totalRows: number;
      readonly percentage: number;
    };
    readonly results?: {
      readonly successCount: number;
      readonly failureCount: number;
      readonly newRecords: number;
      readonly updatedRecords: number;
    };
    readonly estimatedTimeRemaining?: number;
  };
  readonly error?: {
    readonly code: string;
    readonly message: string;
  };
}

/**
 * CSVデータを処理する
 * @param headers ヘッダー行
 * @param rows データ行
 * @param timeRangeStart 開始時刻（HH:mm形式）
 * @param timeRangeEnd 終了時刻（HH:mm形式）
 * @returns 処理結果
 */
export const processCsvData = async (
  headers: readonly string[],
  rows: readonly (readonly string[])[],
  timeRangeStart?: string,
  timeRangeEnd?: string
): Promise<CsvProcessResponse> => {
  // ヘッダーを正規化（バックエンドAPIが期待する形式に変換）
  const normalizedHeaders = headers.map(normalizeHeader);

  const requestBody = {
    csv_data: {
      headers: normalizedHeaders,
      rows: rows.map(row => [...row]),
    },
    time_range_start: timeRangeStart,
    time_range_end: timeRangeEnd,
  };

  const response = await apiClient.post<CsvProcessResponse>(
    API_ENDPOINTS.PROCESS_CSV,
    requestBody,
    API_OPTIONS
  );

  if (!response.data.success) {
    // エラーレスポンスの場合、ErrorResponse型として扱う
    const errorData = response.data as unknown as { error?: { message?: string } };
    throw new Error(errorData.error?.message || 'CSV処理に失敗しました');
  }

  return response.data;
};

/**
 * CSVデータを登録する
 * @param headers ヘッダー行
 * @param rows データ行
 * @param fileName ファイル名
 * @param fileSize ファイルサイズ
 * @param userId ユーザーID（デフォルト: 1）
 * @returns 登録結果
 */
export const registerCsvData = async (
  headers: readonly string[],
  rows: readonly (readonly string[])[],
  fileName: string,
  fileSize: number,
  userId: number = 1
): Promise<RegisterCsvResponse> => {
  const request: RegisterCsvRequest = {
    csvData: {
      headers: [...headers],
      rows: rows.map(row => [...row]),
    },
    metadata: {
      fileName,
      fileSize,
      encoding: 'UTF-8',
      uploadedAt: new Date().toISOString(),
    },
    user_id: userId,
  };

  const response = await apiClient.post<RegisterCsvResponse>(
    API_ENDPOINTS.REGISTER_CSV,
    request,
    API_OPTIONS
  );

  return response.data;
};

/**
 * 登録ステータスを確認する
 * @param registrationId 登録ID
 * @returns ステータスレスポンス
 */
export const checkRegistrationStatus = async (
  registrationId: string
): Promise<StatusCheckResponse> => {
  const response = await apiClient.get<StatusCheckResponse>(
    API_ENDPOINTS.CHECK_STATUS(registrationId),
    API_OPTIONS
  );

  return response.data;
};

/**
 * 登録をキャンセルする
 * @param registrationId 登録ID
 * @returns 成功フラグ
 */
export const cancelRegistration = async (
  registrationId: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(
    API_ENDPOINTS.CHECK_STATUS(registrationId),
    API_OPTIONS
  );

  return response.data;
};
